import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { AttendancePrompt } from 'components/meeting/AttendancePrompt';
import { InviteFromPastMeetings } from 'components/meeting/InviteFromPastMeetings';
import { MeetingInviteSection } from 'components/meeting/MeetingInviteSection';
import { MeetingRepository } from 'repository/meetingRepository';
import { meetingStore } from 'store/meetingStore';
import { shareMeeting } from 'common/utils/share';
import { MapContainer } from 'components/map';
import { ExternalMapSheet } from 'components/map/ExternalMapSheet';
import { markOnboardingCompleted } from 'common/utils/onboarding';
import { isNativeApp, syncNativeMeetingSchedule } from 'common/webview';
import {
    AppScreen, Button, ButtonFooter, InfoGrid, InfoItem, PageIntro, Section,
} from 'components/ds';

const STORAGE_KEY = 'meeting_create_data';

// ── 인라인 SVG 아이콘 (이모지 금지)
export const MeetingShare = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const createdMeeting = location.state?.meeting;
    const savedData = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');

    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const weekdays = t('common.weekDaysShort', { returnObjects: true }) as string[];
        const weekday = weekdays[date.getDay()];
        return t('meetingShare.dateFormat', { year, month, day, weekday });
    };

    const stateData = location.state || {};
    const meetingData = {
        name: stateData.meetingName || savedData.meetingName || createdMeeting?.meetingName || '',
        date: (stateData.selectedDate || savedData.selectedDate) ? formatDate(new Date(stateData.selectedDate || savedData.selectedDate)) : '',
        time: stateData.selectedTime || savedData.selectedTime || '',
        location: stateData.selectedPlace || savedData.selectedPlace || savedData.selectedPlaceInfo?.name || '',
        inviteCode: createdMeeting?.inviteCode || '',
        meetingType: stateData.meetingType || savedData.meetingType || 'DAY',
        travelDestination: stateData.travelDestination || savedData.travelDestination || '',
        travelStartDate: stateData.travelStartDate || savedData.travelStartDate || '',
        travelEndDate: stateData.travelEndDate || savedData.travelEndDate || '',
        hotelName: stateData.hotelName || savedData.hotelName || savedData.hotelInfo?.name || '',
        isRegularMeeting: !!savedData.isRegularMeeting,
        repeatType: savedData.repeatType as string | null | undefined,
        repeatInterval: savedData.repeatInterval as number | null | undefined,
        repeatDaysOfWeek: savedData.repeatDaysOfWeek as string | null | undefined,
        repeatDayOfMonth: savedData.repeatDayOfMonth as number | null | undefined,
        repeatTime: savedData.repeatTime as string | null | undefined,
        /* 장소 미니지도용 — 장소 고르기에서 넘어온 placeInfo 에 좌표가 들어 있습니다.
           생성 응답(createdMeeting)에도 있어 둘 다 봅니다. */
        placeAddress: (stateData.selectedPlaceInfo || savedData.selectedPlaceInfo)?.address || '',
        lat: Number((stateData.selectedPlaceInfo || savedData.selectedPlaceInfo)?.latitude
            ?? (stateData.selectedPlaceInfo || savedData.selectedPlaceInfo)?.lat
            ?? createdMeeting?.latitude) || null,
        lng: Number((stateData.selectedPlaceInfo || savedData.selectedPlaceInfo)?.longitude
            ?? (stateData.selectedPlaceInfo || savedData.selectedPlaceInfo)?.lng
            ?? createdMeeting?.longitude) || null,
    };

    const DOW_ORDER = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const repeatSummary = (() => {
        if (!meetingData.isRegularMeeting || !meetingData.repeatType) return '';
        const time = meetingData.repeatTime ? ` ${meetingData.repeatTime}` : '';
        if (meetingData.repeatType === 'DAILY') return t('meeting.repeatDaily', { time });
        if (meetingData.repeatType === 'WEEKLY') {
            const prefix = meetingData.repeatInterval === 2 ? t('meeting.biweeklyPrefix') : t('meeting.weeklyPrefix');
            const wd = t('common.weekDaysShort', { returnObjects: true }) as string[];
            const days = (meetingData.repeatDaysOfWeek || '')
                .split(',').map(s => s.trim()).filter(Boolean)
                .sort((a, b) => DOW_ORDER.indexOf(a) - DOW_ORDER.indexOf(b))
                .map(v => { const i = DOW_ORDER.indexOf(v); return i >= 0 ? wd[i] : v; }).join('·');
            return `${prefix}${days ? ' ' + days : ''}${time}`.trim();
        }
        if (meetingData.repeatType === 'MONTHLY') {
            return t('meeting.repeatMonthly', { day: meetingData.repeatDayOfMonth || 1, time });
        }
        return '';
    })();

    useEffect(() => () => { sessionStorage.removeItem(STORAGE_KEY); }, []);

    const meetingId = createdMeeting?.meetingId;
    // 모임 생성 직후 — 자동으로 참석 여부 prompt 띄움. 사용자가 [홈으로] 로 빠져나가도
    // 참석/위치공유 결정이 누락되지 않도록 (이전엔 모임 상세 진입 시에만 떴음).
    const [showAttendance, setShowAttendance] = useState(!!meetingId);
    const [showInviteFromPast, setShowInviteFromPast] = useState(false);
    /* 장소 미니지도를 눌렀을 때 뜨는 외부 지도앱 고르기 */
    const [showExternalMap, setShowExternalMap] = useState(false);

    const handleShare = async () => {
        const success = await shareMeeting({
            meetingName: meetingData.name,
            date: meetingData.date,
            time: meetingData.time,
            location: meetingData.location,
            inviteCode: meetingData.inviteCode,
        });
        if (success) {
            markOnboardingCompleted('step_invite');
        } else {
            await showAlert({ message: t('meetingShare.shareUnavailable') });
        }
    };

    const handleAttendanceSubmit = async (status: 'ATTENDING' | 'NOT_ATTENDING' | 'UNDECIDED', locationSharing: boolean) => {
        if (!meetingId) {
            setShowAttendance(false);
            return;
        }
        const res: any = await MeetingRepository.onPutMyMembership(meetingId, { attendanceStatus: status, locationSharing });
        const ok = res?.status >= 200 && res?.status < 300;
        if (!ok) {
            const msg = res?.data?.message || res?.message || t('meetingShare.saveFailed', { status: res?.status || 'network' });
            setShowAttendance(false);
            await showAlert({ message: msg });
            return;
        }
        localStorage.setItem(`locationSharing_${meetingId}`, String(locationSharing));

        if (status === 'ATTENDING' && locationSharing && isNativeApp()) {
            const scheduleDate = stateData.selectedDate || savedData.selectedDate;
            const scheduleTime = stateData.selectedTime || savedData.selectedTime;
            if (scheduleDate) {
                // 실제 시작 시각 그대로 등록 — 시작 1시간 전 추적 개시는 네이티브가 판단한다.
                // (예전엔 1시간 이내일 때만 now 로 당겨 등록해, 그보다 먼 모임은 홈을 거치지
                //  않으면 영영 등록되지 않았다)
                const startStr = scheduleTime ? `${scheduleDate}T${scheduleTime}` : scheduleDate;
                const placeInfo = stateData.selectedPlaceInfo || savedData.selectedPlaceInfo;
                syncNativeMeetingSchedule(meetingId, {
                    startDatetime: startStr,
                    latitude: placeInfo?.latitude ?? placeInfo?.lat ?? createdMeeting?.latitude,
                    longitude: placeInfo?.longitude ?? placeInfo?.lng ?? placeInfo?.lon ?? createdMeeting?.longitude,
                });
            }
        }

        meetingStore.onGetMeetingList();
        meetingStore.onGetMeetingMembers(meetingId);
        setShowAttendance(false);
    };

    // 모바일 WebView/일부 브라우저에서 react-router navigate() 가 먹통인 회귀가 반복되어
    // 네이티브 location.replace 로 이동을 강제 — 풀 리로드지만 확실히 동작함.
    const markCreated = () => {
        try { sessionStorage.setItem('meeting_create_completed', '1'); } catch {}
        try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    };

    const handleGoToDetail = () => {
        if (!meetingId) return;
        markCreated();
        window.location.replace(`/meeting/detail/${meetingId}?att=1`);
    };

    const handleComplete = () => {
        markCreated();
        window.location.replace('/home');
    };

    /* 모임 정보 — 값이 있는 것만 */
    const infoItems: InfoItem[] = [];
    if (meetingData.name) infoItems.push({ label: t('meetingShare.name'), value: meetingData.name, full: true });
    if (meetingData.date) infoItems.push({ label: t('meetingShare.date'), value: meetingData.date });
    if (meetingData.time) infoItems.push({ label: t('meetingShare.time'), value: meetingData.time });
    if (repeatSummary) infoItems.push({ label: t('meetingShare.repeatLabel'), value: repeatSummary });
    if (meetingData.meetingType === 'TRAVEL' && meetingData.travelDestination) {
        infoItems.push({ label: t('meetingShare.travelDestination'), value: meetingData.travelDestination });
    }
    if (meetingData.meetingType === 'TRAVEL' && meetingData.travelStartDate) {
        infoItems.push({
            label: t('meetingShare.travelPeriod'),
            value: `${formatDate(new Date(meetingData.travelStartDate))}${meetingData.travelEndDate
                ? ` ~ ${formatDate(new Date(meetingData.travelEndDate))}`
                : ''}`,
            full: true,
        });
    }
    infoItems.push({
        label: meetingData.meetingType === 'TRAVEL' ? t('meetingShare.gatherPlace') : t('meetingShare.place'),
        value: meetingData.location || '-',
        muted: !meetingData.location,
        full: true,
    });
    if (meetingData.hotelName) {
        infoItems.push({ label: t('meetingShare.hotel'), value: meetingData.hotelName, full: true });
    }

    return (
        <AppScreen
            title={t('meetingShare.headerTitle')}
            bar="close"
            hasFooter
            onClose={handleComplete}
        >
            <PageIntro title={t('meetingShare.doneTitle')} desc={t('meetingShare.doneSubtitle')} />

            <Section spacing="tight">
                <InfoGrid items={infoItems} />

                {/* 장소 미니지도 — 모임 전이라 "어디로 가나" 가 가장 급합니다.
                    누르면 카카오맵·네이버지도·티맵으로 바로 길찾기할 수 있게 시트를 엽니다. */}
                {meetingData.lat && meetingData.lng ? (
                    <div
                        style={{ marginTop: 'var(--t-space-4)', cursor: 'pointer' }}
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowExternalMap(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setShowExternalMap(true); }}
                    >
                        <MapContainer
                            center={{ lat: meetingData.lat, lng: meetingData.lng }}
                            markers={[{
                                id: 'place',
                                lat: meetingData.lat,
                                lng: meetingData.lng,
                                label: '',
                                tooltip: meetingData.location,
                                isDestination: true,
                            }]}
                            height={160}
                            zoomLevel={5}
                            placeName={meetingData.location}
                        />
                    </div>
                ) : null}
            </Section>

            {/* 친구 초대 — 모임 상세와 같은 카드 */}
            {meetingId && (
                <Section divided last>
                    <MeetingInviteSection
                        meetingId={meetingId}
                        inviteCode={meetingData.inviteCode}
                        onShare={handleShare}
                        onOpenInviteFromPast={() => setShowInviteFromPast(true)}
                    />
                </Section>
            )}

            <ButtonFooter>
                <Button variant="ghost" size="lg" onClick={handleComplete}>
                    {t('meetingShare.goHome')}
                </Button>
                {meetingId && (
                    <Button variant="primary" size="lg" block onClick={handleGoToDetail}>
                        {t('meetingShare.goDetail')}
                    </Button>
                )}
            </ButtonFooter>

            <AttendancePrompt
                visible={showAttendance}
                meetingId={meetingId}
                meetingName={meetingData.name}
                meetingDate={meetingData.date}
                meetingTime={meetingData.time}
                meetingLocation={meetingData.location}
                initialStatus="ATTENDING"
                onSubmit={handleAttendanceSubmit}
                onDismiss={() => setShowAttendance(false)}
            />
            {meetingId && (
                <InviteFromPastMeetings
                    open={showInviteFromPast}
                    onClose={() => setShowInviteFromPast(false)}
                    targetMeetingId={meetingId}
                    currentUserNo={Number(sessionStorage.getItem('userNo')) || undefined}
                />
            )}

            <ExternalMapSheet
                visible={showExternalMap}
                placeName={meetingData.location}
                address={meetingData.placeAddress}
                lat={meetingData.lat || 0}
                lng={meetingData.lng || 0}
                onClose={() => setShowExternalMap(false)}
            />
        </AppScreen>
    );
};
